import { createFileRoute } from "@tanstack/react-router"
import { Check, X, Zap, AlertCircle, ArrowRight } from "lucide-react"
import { Button } from "@/components/ui/button"
import { createLLMOMeta, createBreadcrumbSchema, createFAQSchema, createArticleSchema } from "@/lib/seo"
import { useAuth } from "@/components/auth/AuthProvider"
import { useAnalytics } from "@/lib/analytics"
import { Paragraph, ContentSection } from "@/components/tools/SeoContent"
import { useState } from "react"
import { AskAIExplain } from "@/components/ask-ai-to-explain"
import { LifetimeAccessPricing } from "@/components/life-time-access-pricing"

// ─── FAQ Data ────────────────────────────────────────────────
const faqs = [
  {
    question: "Is Zapify better than Zorcha for Instagram automation in 2026?",
    answer:
      "For creators who want intelligent automation, yes. Zorcha offers unlimited messaging at a flat rate, but it still relies on keyword matching. Zapify uses AI intent recognition that understands what followers mean - not just exact keywords - plus it includes safety features like DM queue, overflow protection, and SendBack for missed messages.",
  },
  {
    question: "How does Zorcha's pricing compare to Zapify?",
    answer:
      "Zorcha charges $14.99/month for unlimited messages with no caps. Zapify offers a forever-free tier (1,000 DMs/month), then $19/month for 25,000 DMs. While Zorcha's flat rate looks cheaper on paper, Zapify's free tier covers most small creators, and the Pro plan adds AI intelligence, more trigger types, and safety features that Zorcha doesn't have.",
  },
  {
    question: "Does Zorcha have AI-powered automation?",
    answer:
      "Not yet. Zorcha lists 'AI Persona' as 'SOON' on their feature page, meaning it hasn't shipped. Today, Zorcha uses keyword matching for triggers. Zapify is the only platform in 2026 that offers production-ready AI intent recognition - understanding natural language like 'can you send me that?' without requiring exact keyword matches.",
  },
  {
    question: "Can I switch from Zorcha to Zapify easily?",
    answer:
      "Yes. Zapify connects via Instagram Login or Facebook Login in under 2 minutes. Pre-built AI templates mean you can recreate your Zorcha automations - and upgrade them with AI intelligence - within 5 minutes of signing up.",
  },
  {
    question: "Does Zorcha support Next Post and New Follower automations?",
    answer:
      "No. Zorcha currently lacks Next Post automation and New Follower DM triggers. Zapify supports both, along with comment-to-DM, story reply, keyword trigger, IG Live auto-reply, and Reel triggers - all powered by AI intent recognition.",
  },
  {
    question: "Is Zorcha safe and reliable? How does it compare to Zapify's safety features?",
    answer:
      "Zorcha is a relatively new platform, which raises trust and reliability concerns. It doesn't offer DM safety queue, overflow protection, or rate limit shielding. Zapify is an approved Meta Business Partner with built-in safety features: DM queue, overflow protection, auto-pause for rate limits, slow-down mode for viral posts, and SendBack for missed DMs.",
  },
]

// ─── Feature Table Data ──────────────────────────────────────
type FeatureRow = {
  name: string
  zapify: string | boolean
  zorcha: string | boolean
  zapifyNote?: string
  zorchaNote?: string
}

type FeatureCategory = {
  category: string
  rows: FeatureRow[]
}

const featureCategories: FeatureCategory[] = [
  {
    category: "AI & Intelligence",
    rows: [
      { name: "AI Intent Recognition", zapify: true, zorcha: false, zapifyNote: "Understands context", zorchaNote: "Keyword-only" },
      { name: "AI-Generated Triggers", zapify: true, zorcha: false },
      { name: "AI Persona / Smart Routing", zapify: true, zorcha: false, zorchaNote: "Coming soon" },
    ],
  },
  {
    category: "Instagram Triggers",
    rows: [
      { name: "Comment-to-DM Automation", zapify: true, zorcha: true },
      { name: "Story Reply / Mention DM", zapify: true, zorcha: true },
      { name: "Reel / Post Keyword Trigger", zapify: true, zorcha: true },
      { name: "IG Live Auto-Reply", zapify: true, zorcha: true },
      { name: "New Follower Automation", zapify: true, zorcha: false },
      { name: "Next Post / Upcoming Automation", zapify: true, zorcha: false },
    ],
  },
  {
    category: "Safety & Reliability",
    rows: [
      { name: "SendBack / Rewind Missed DMs", zapify: true, zorcha: false },
      { name: "DM Safety Queue", zapify: true, zorcha: false },
      { name: "DM Overflow Protection", zapify: true, zorcha: false },
      { name: "Auto DM Pause (Rate Limit Shield)", zapify: true, zorcha: false },
      { name: "Slow-Down Mode (Viral Posts)", zapify: true, zorcha: false },
    ],
  },
  {
    category: "Setup & Experience",
    rows: [
      { name: "Setup Time", zapify: "~5 min", zorcha: "~5 min", zapifyNote: "AI templates", zorchaNote: "Simple UI" },
      { name: "Template Library", zapify: true, zorcha: false },
      { name: "Real-Time Phone Preview", zapify: true, zorcha: false },
      { name: "Mobile App", zapify: true, zorcha: false },
    ],
  },
  {
    category: "Pricing & Compliance",
    rows: [
      { name: "Pricing Model", zapify: "DM Volume", zorcha: "Unlimited Flat" },
      { name: "Forever Free Tier", zapify: true, zorcha: false },
      { name: "Meta Business Partner", zapify: true, zorcha: false },
      { name: "Established Track Record", zapify: true, zorcha: false, zorchaNote: "Very new" },
    ],
  },
]

// ─── Pricing Data ────────────────────────────────────────────
const pricingTiers = [
  { label: "Getting started / free plan", zapify: "$0 for 30 days", zorcha: "$0 Free Forever", zapifyWidth: "2%", zorchaWidth: "2%" },
  { label: "Entry paid plan", zapify: "$9.99/mo", zorcha: "$9.99/mo (Pro)", zapifyWidth: "20%", zorchaWidth: "20%" },
  { label: "Professional / Ultimate", zapify: "$19.99/mo", zorcha: "$24.99/mo", zapifyWidth: "38%", zorchaWidth: "50%" },
  { label: "High-volume / Business", zapify: "$19.99/mo", zorcha: "$79.99/mo", zapifyWidth: "38%", zorchaWidth: "100%" },
  { label: "Lifetime access", zapify: "$200 once", zorcha: "Not available", zapifyWidth: "30%", zorchaWidth: "0%" },
]

// ─── Route ───────────────────────────────────────────────────
export const Route = createFileRoute("/comparison/zorcha-vs-zapify-pro")({
  head: () => ({
    meta: [
      ...createLLMOMeta({
        title: "Zorcha vs Zapify Pro 2026: Instagram Automation Comparison | Zapify",
        description:
          "Zorcha vs Zapify Pro comparison for 2026. Zorcha offers unlimited messaging, but Zapify delivers AI intent recognition, more triggers, and proven safety features. Full side-by-side breakdown.",
        url: "https://zapify.pro/comparison/zorcha-vs-zapify-pro",
        publishedTime: "2026-03-20",
        modifiedTime: "2026-06-06",
        ogType: "article",
      }),
      { property: "og:image", content: "https://cms.zapify.pro/uploads/OG_image1_f82f5871e2.png" },
      { property: "og:locale", content: "en_US" },
      { name: "keywords", content: "Zorcha vs Zapify, Zorcha alternative 2026, Instagram DM automation, AI Instagram automation, Zorcha review, Instagram comment to DM, Instagram growth tool 2026" },
    ],
    links: [
      { rel: "canonical", href: "https://zapify.pro/comparison/zorcha-vs-zapify-pro" },
    ],
    scripts: [
      createBreadcrumbSchema({
        itemListElement: [
          { position: 1, name: "Home", item: "https://zapify.pro" },
          { position: 2, name: "Comparisons", item: "https://zapify.pro/comparison" },
          { position: 3, name: "Zorcha vs Zapify", item: "https://zapify.pro/comparison/zorcha-vs-zapify-pro" },
        ],
      }),
      createFAQSchema(faqs),
      createArticleSchema({
        headline: "Zorcha vs Zapify Pro 2026: Instagram DM Automation Compared",
        description: "Comprehensive comparison of Zorcha and Zapify Pro for Instagram DM automation in 2026.",
        image: "https://cms.zapify.pro/uploads/OG_image1_f82f5871e2.png",
        author: { name: "Zapify", url: "https://zapify.pro" },
        datePublished: "2026-03-20",
        dateModified: "2026-06-06",
        publisher: {
          name: "Zapify Pro",
          url: "https://zapify.pro",
          logo: "https://zapify.pro/logo-small.png",
          description: "AI-Powered Instagram Comment Management & Automation",
        },
      }),
    ],
  }),
  component: ZorchaComparisonPage,
})

// ─── Page Component ──────────────────────────────────────────
export function ZorchaComparisonPage() {
  const { track } = useAnalytics()
  const { openAuth } = useAuth()
  const [openFaq, setOpenFaq] = useState(0)

  const handleCTA = (ctaId: string, buttonText: string) => {
    track("cta_clicked", { cta_id: ctaId, button_text: buttonText })
    openAuth()
  }

  return (
    <div className="min-h-screen relative overflow-hidden bg-white dark:bg-black transition-colors duration-300">
      {/* Radial fade background */}
      <div className="absolute inset-0 bg-white/50 dark:bg-black/20 [mask-image:radial-gradient(ellipse_60%_50%_at_50%_0%,#000_70%,transparent_100%)] pointer-events-none" />
      {/* Dotted background */}
      <div className="absolute inset-0 bg-[radial-gradient(circle,#80808015_1px,transparent_1px)] bg-[size:20px_20px] pointer-events-none" />

      <div className="relative z-10 pt-28 sm:pt-36 pb-12">
        {/* ═══ Section 1: Hero ═══ */}
        <section className="max-w-7xl mx-auto px-4 sm:px-6 text-center mb-16 sm:mb-20">
          <div className="inline-flex items-center justify-center px-4 py-1.5 rounded-full bg-white dark:bg-neutral-900 border border-neutral-100 dark:border-neutral-800 shadow-sm mb-6">
            <span className="text-neutral-600 dark:text-neutral-300 font-semibold text-xs">Updated June 2026</span>
          </div>

          <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold text-neutral-900 dark:text-white mb-4 sm:mb-6 tracking-tight max-w-5xl mx-auto">
            Zorcha vs{" "}
            <span className="gradient-text">Zapify Pro</span>
            : Instagram DM Automation Compared for 2026
          </h1>
          <p className="text-base sm:text-lg md:text-xl text-neutral-600 dark:text-neutral-400 font-medium max-w-2xl mx-auto mb-8 sm:mb-10">
            Zorcha covers core DM automation. Zapify delivers AI-powered growth attribution, smart offers, templates, and lifetime access. See which platform actually helps you grow.
          </p>

          <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 justify-center">
            <Button
              onClick={() => handleCTA("CTA_Comparison_Hero", "Start Free No Credit Card")}
              className="bg-primary-gradient text-primary-foreground hover:brightness-[0.97] active:brightness-[0.92] font-bold px-8 py-6 text-base rounded-full shadow-lg"
            >
              Start Free No Credit Card
              <ArrowRight className="ml-2 h-4 w-4" />
            </Button>
            <Button
              variant="outline"
              className="px-8 py-6 text-base rounded-full font-semibold border-neutral-200 dark:border-neutral-700"
              onClick={() => {
                document.getElementById("feature-table")?.scrollIntoView({ behavior: "smooth" })
              }}
            >
              See Full Comparison
            </Button>
          </div>
        </section>

        {/* ═══ Ask AI to Explain ═══ */}
        <section className="max-w-3xl mx-auto px-4 sm:px-6 mb-16 sm:mb-20">
          <AskAIExplain
            context="comparison"
            meta={{ competitorName: "Zorcha", pageUrl: "https://zapify.pro/comparison/zorcha-vs-zapify-pro" }}
            isCard
            isWidget
          />
        </section>

        {/* ═══ Section 2: Quick Answer Box ═══ */}
        <section className="max-w-3xl mx-auto px-4 sm:px-6 mb-16 sm:mb-20" aria-label="Featured summary">
          <article className="rounded-2xl border border-neutral-200 dark:border-neutral-800 bg-white dark:bg-neutral-900/50 p-6 sm:p-8 shadow-sm">
            <p className="text-xs font-bold uppercase tracking-widest text-brand-button mb-3">
              Quick Answer - How does Zorcha compare to Zapify?
            </p>
            <p className="text-sm sm:text-base text-neutral-600 dark:text-neutral-400 leading-relaxed">
              <strong className="text-neutral-900 dark:text-white">Zorcha covers core Instagram DM automation, but Zapify is built around the full creator growth loop.</strong>{" "}
              Zapify adds{" "}
              <strong className="bg-clip-text text-transparent bg-gradient-to-r from-brand-accent to-brand-accent">
                AI-powered replies with brand voice controls
              </strong>
              , growth attribution showing followers and leads gained, smart offers, templates, 10+ language support, and a possible $200 lifetime access offer. Zorcha's public pricing also introduces AI credit tiers, higher recurring plans up to $79.99/month, and several features marked "Soon." For creators who want proven growth features and simpler economics, Zapify is the stronger choice.
            </p>
          </article>
        </section>

        {/* ═══ Section 3: Feature Comparison Table ═══ */}
        <section id="feature-table" className="max-w-7xl mx-auto px-4 sm:px-6 mb-16 sm:mb-20">
          <div className="text-center mb-10 sm:mb-12">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-neutral-900 dark:text-white tracking-tight">
              How does Zapify compare to Zorcha feature{" "}
              <span className="gradient-text">by feature?</span>
            </h2>
            <Paragraph className="max-w-3xl mx-auto">
              A transparent, side-by-side breakdown of every Instagram automation capability that matters in 2026.
            </Paragraph>
          </div>

          <div className="rounded-2xl border border-neutral-200 dark:border-neutral-800 overflow-hidden shadow-sm bg-white dark:bg-neutral-900/50">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-neutral-50/80 dark:bg-neutral-900/80 border-b border-neutral-200 dark:border-neutral-800">
                    <th className="text-left px-4 sm:px-6 py-4 font-semibold text-neutral-500 dark:text-neutral-400 uppercase tracking-wider text-xs w-[40%]">
                      Feature
                    </th>
                    <th className="text-left px-4 sm:px-6 py-4 uppercase tracking-wider text-xs w-[30%] text-green-700 dark:text-green-300">
                      Zapify
                    </th>
                    <th className="text-left px-4 sm:px-6 py-4 font-semibold text-neutral-500 dark:text-neutral-400 uppercase tracking-wider text-xs w-[30%]">
                      Zorcha
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-neutral-100 dark:divide-neutral-800">
                  {featureCategories.flatMap((cat) => [
                    <tr key={`cat-${cat.category}`} className="bg-neutral-50/70 dark:bg-neutral-900/70">
                      <td colSpan={3} className="px-4 sm:px-6 py-2.5 text-xs font-bold uppercase tracking-widest text-brand-button">
                        {cat.category}
                      </td>
                    </tr>,
                    ...cat.rows.map((row) => (
                      <tr key={`${cat.category}-${row.name}`} className="hover:bg-neutral-50/50 dark:hover:bg-neutral-800/20 transition-colors align-top">
                        <td className="px-4 sm:px-6 py-3.5 font-medium text-neutral-700 dark:text-neutral-300">{row.name}</td>
                        <td className="px-4 sm:px-6 py-3.5">
                          <div className="flex flex-col gap-1.5">
                            <span className="text-sm font-semibold text-neutral-800 dark:text-neutral-100">{typeof row.zapify === "boolean" ? (row.zapify ? "Yes" : "No") : row.zapify}</span>
                            {row.zapifyNote ? <span className="text-xs text-green-700 dark:text-green-300">{row.zapifyNote}</span> : null}
                          </div>
                        </td>
                        <td className="px-4 sm:px-6 py-3.5">
                          <div className="flex flex-col gap-1.5">
                            <span className="text-sm font-semibold text-neutral-700 dark:text-neutral-200">{typeof row.zorcha === "boolean" ? (row.zorcha ? "Yes" : "No") : row.zorcha}</span>
                            {row.zorchaNote ? <span className="text-xs text-red-600 dark:text-red-300">{row.zorchaNote}</span> : null}
                          </div>
                        </td>
                      </tr>
                    )),
                  ])}
                </tbody>
              </table>
            </div>
          </div>
        </section>

        {/* ═══ Section 4: Pricing Breakdown ═══ */}
        <section className="max-w-7xl mx-auto px-4 sm:px-6 mb-16 sm:mb-20">
          <div className="text-center mb-10 sm:mb-12">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-neutral-900 dark:text-white tracking-tight">
              Zorcha's pricing vs{" "}
              <span className="gradient-text">Zapify's growth-first pricing</span>
            </h2>
            <Paragraph className="max-w-2xl mx-auto">
              Zorcha's tiers go up to $79.99/month. Zapify starts free, stays flat, and offers lifetime access for $200.
            </Paragraph>
          </div>

          <div className="rounded-2xl border border-neutral-200 dark:border-neutral-800 bg-white dark:bg-neutral-900/50 overflow-hidden">
            <table className="w-full border-collapse">
              <thead>
                <tr className="bg-neutral-50/80 dark:bg-neutral-900/80 border-b border-neutral-200 dark:border-neutral-800">
                  <th className="text-left px-4 sm:px-5 py-3 text-xs font-semibold uppercase tracking-wide text-neutral-500 dark:text-neutral-400">Plan stage</th>
                  <th className="text-left px-4 sm:px-5 py-3 text-xs font-semibold uppercase tracking-wide text-green-700 dark:text-green-300">Zapify</th>
                  <th className="text-left px-4 sm:px-5 py-3 text-xs font-semibold uppercase tracking-wide text-neutral-600 dark:text-neutral-300">Zorcha</th>
                </tr>
              </thead>
              <tbody>
                {pricingTiers.map((tier) => (
                  <tr key={tier.label} className="align-top border-b last:border-b-0 border-neutral-100 dark:border-neutral-800/80">
                    <td className="px-4 sm:px-5 py-4 text-sm font-semibold text-neutral-800 dark:text-neutral-100">{tier.label}</td>
                    <td className="px-4 sm:px-5 py-4 text-lg font-black tracking-tight text-green-700 dark:text-green-300">{tier.zapify}</td>
                    <td className="px-4 sm:px-5 py-4 text-lg font-black tracking-tight text-neutral-700 dark:text-neutral-100">{tier.zorcha}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>

        {/* ═══ Lifetime Access Pricing ═══ */}
        <LifetimeAccessPricing
          spotsLeft={87}
          totalSpots={100}
          price={200}
          originalMonthlyPrice={20}
          onGetAccess={() => handleCTA("CTA_Zorcha_Lifetime_Pricing", "Claim Lifetime Access")}
        />

        {/* ═══ Section 5: AI vs Keywords ═══ */}
        <section className="max-w-7xl mx-auto px-4 sm:px-6 mb-16 sm:mb-20">
          <div className="text-center mb-10 sm:mb-12">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-neutral-900 dark:text-white tracking-tight">
              AI Intent Recognition vs{" "}
              <span className="gradient-text">Keyword Matching</span>
            </h2>
            <Paragraph className="max-w-2xl mx-auto">
              Zorcha uses keyword triggers like every other tool on the market. Zapify is the only platform in 2026 with production-ready AI that understands natural language.
            </Paragraph>
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            {/* Zapify AI */}
            <div className="rounded-2xl border-2 border-green-200 dark:border-green-800/50 bg-white dark:bg-neutral-900/50 p-6 sm:p-8">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-10 h-10 rounded-lg bg-green-100 dark:bg-green-900/30 flex items-center justify-center">
                  <Zap className="w-5 h-5 text-green-600 dark:text-green-400" />
                </div>
                <h3 className="text-lg font-bold text-green-600 dark:text-green-400">Zapify - AI Intent</h3>
              </div>

              <p className="text-neutral-600 dark:text-neutral-400 text-sm mb-3">
                User comments: <em className="text-neutral-900 dark:text-white font-medium">"this looks amazing, how do I get it?"</em>
              </p>
              <div className="bg-green-50 dark:bg-green-900/20 rounded-lg px-4 py-3 text-sm text-green-700 dark:text-green-300 mb-4">
                <Check className="inline w-4 h-4 mr-1" /> AI understands buying intent - Sends product DM automatically
              </div>

              <p className="text-neutral-600 dark:text-neutral-400 text-sm mb-3">
                User comments: <em className="text-neutral-900 dark:text-white font-medium">"need this in my life!!"</em>
              </p>
              <div className="bg-green-50 dark:bg-green-900/20 rounded-lg px-4 py-3 text-sm text-green-700 dark:text-green-300">
                <Check className="inline w-4 h-4 mr-1" /> AI recognizes desire - Triggers sales sequence
              </div>

              <p className="text-xs text-neutral-400 dark:text-neutral-500 mt-4">
                No keyword configuration required. Works out of the box.
              </p>
            </div>

            {/* Zorcha Keywords */}
            <div className="rounded-2xl border-2 border-red-200 dark:border-red-800/50 bg-white dark:bg-neutral-900/50 p-6 sm:p-8">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-10 h-10 rounded-lg bg-red-100 dark:bg-red-900/30 flex items-center justify-center">
                  <AlertCircle className="w-5 h-5 text-red-500 dark:text-red-400" />
                </div>
                <h3 className="text-lg font-bold text-red-500 dark:text-red-400">Zorcha - Keywords</h3>
              </div>

              <p className="text-neutral-600 dark:text-neutral-400 text-sm mb-3">
                User comments: <em className="text-neutral-900 dark:text-white font-medium">"this looks amazing, how do I get it?"</em>
              </p>
              <div className="bg-red-50 dark:bg-red-900/20 rounded-lg px-4 py-3 text-sm text-red-700 dark:text-red-300 mb-4">
                <X className="inline w-4 h-4 mr-1" /> No match - keyword was "LINK" (exact match only)
              </div>

              <p className="text-neutral-600 dark:text-neutral-400 text-sm mb-3">
                User comments: <em className="text-neutral-900 dark:text-white font-medium">"need this in my life!!"</em>
              </p>
              <div className="bg-red-50 dark:bg-red-900/20 rounded-lg px-4 py-3 text-sm text-red-700 dark:text-red-300">
                <X className="inline w-4 h-4 mr-1" /> No match - keyword was "BUY" not "need"
              </div>

              <p className="text-xs text-neutral-400 dark:text-neutral-500 mt-4">
                Requires manual keyword setup for every trigger. AI persona is listed as "coming soon."
              </p>
            </div>
          </div>
        </section>

        {/* ═══ Section 6: FAQ ═══ */}
        <section className="w-full py-12 sm:py-20 px-4 sm:px-6 border-b border-neutral-100 dark:border-white/5">
          <div className="max-w-5xl mx-auto">
            <div className="mb-8 text-center sm:mb-12">
              <h2 className="text-2xl sm:text-3xl md:text-5xl lg:text-6xl font-bold text-[#181C31] dark:text-white tracking-tight">
                Frequently <span className="gradient-text">Asked Questions</span>
              </h2>
              <p className="text-sm sm:text-base md:text-xl text-[#64657E] dark:text-gray-400 font-medium max-w-2xl mx-auto mt-3 sm:mt-5">
                Everything you need to know about Zorcha vs Zapify
              </p>
            </div>

            <div className="space-y-3">
              {faqs.map((faq, index) => {
                const isOpen = openFaq === index
                return (
                  <div key={faq.question} className="rounded-2xl border border-border/50 bg-card/90 px-4 py-3.5 sm:px-6 sm:py-4 dark:bg-card/80">
                    <button type="button" onClick={() => setOpenFaq((prev) => (prev === index ? -1 : index))} className="flex w-full items-center justify-between gap-3 text-left">
                      <div className="flex items-center gap-3">
                        <span className="text-base leading-none">{["⚡", "💸", "🤖", "🔄", "📩", "🛡️"][index] ?? "⚡"}</span>
                        <p className="text-base font-semibold leading-snug text-foreground sm:text-[1.15rem]">{faq.question}</p>
                      </div>
                      <span className="inline-flex size-8 shrink-0 items-center justify-center rounded-full text-muted-foreground">{isOpen ? "−" : "+"}</span>
                    </button>
                    <div className={`grid transition-all duration-300 ${isOpen ? "mt-2.5 grid-rows-[1fr] opacity-100 sm:mt-3" : "grid-rows-[0fr] opacity-0"}`}>
                      <p className="overflow-hidden pr-3 text-[13px] leading-relaxed text-muted-foreground sm:pr-8 sm:text-[0.95rem]">{faq.answer}</p>
                    </div>
                  </div>
                )
              })}
            </div>
          </div>
        </section>

        {/* ═══ Section 7: Bottom CTA ═══ */}
        <section className="relative overflow-hidden py-16 sm:py-24 px-4 sm:px-6">
          {/* Gradient blob */}
          <div className="absolute inset-0 pointer-events-none" aria-hidden="true">
            <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[500px] h-[500px] bg-brand-accent/10 rounded-full blur-3xl" />
          </div>

          <div className="relative max-w-2xl mx-auto text-center">
            <h2 className="text-2xl sm:text-4xl md:text-5xl font-bold text-neutral-900 dark:text-white mb-4 sm:mb-6 tracking-tight">
              Ready to automate <span className="gradient-text">smarter today?</span>
            </h2>
            <p className="text-base sm:text-lg text-neutral-600 dark:text-neutral-400 mb-8 sm:mb-10 max-w-xl mx-auto">
              Unlimited messages mean nothing without intelligent automation. Try Zapify free and see the difference AI makes.
            </p>

            <Button
              onClick={() => handleCTA("CTA_Comparison_Bottom", "Start Free with Zapify")}
              className="bg-primary-gradient text-primary-foreground hover:brightness-[0.97] active:brightness-[0.92] font-bold px-10 py-6 text-lg rounded-full shadow-lg"
            >
              Start Free with Zapify
              <ArrowRight className="ml-2 h-5 w-5" />
            </Button>

            <p className="mt-4 text-sm text-neutral-500 dark:text-neutral-400">
              Free forever tier &middot; No credit card &middot; 1-minute setup
            </p>
          </div>
        </section>

        {/* Other Comparisons */}
        <section className="max-w-7xl mx-auto px-4 sm:px-6 py-12 sm:py-16 border-b border-neutral-100 dark:border-white/5">
          <div className="text-center mb-8">
            <h2 className="text-xl sm:text-2xl font-bold text-neutral-900 dark:text-white">
              Compare Zapify vs <span className="gradient-text">other tools</span>
            </h2>
          </div>
          <div className="flex flex-wrap justify-center gap-3">
            {[
              { label: "Zapify vs ManyChat", href: "/comparison/manychat-vs-zapify-pro" },
              { label: "Zapify vs LinkDM", href: "/comparison/linkdm-vs-zapify-pro" },
              { label: "Zapify vs InstantDM", href: "/comparison/instantdm-vs-zapify-pro" },
              { label: "Zapify vs ReplyRush", href: "/comparison/replyrush-vs-zapify-pro" },
              { label: "Zapify vs SuperProfile", href: "/comparison/superprofile-vs-zapify-pro" },
            ].map((link) => (
              <a key={link.href} href={link.href} className="px-5 py-2.5 rounded-xl border border-neutral-200 dark:border-neutral-800 bg-white dark:bg-neutral-900/50 text-sm font-semibold text-neutral-700 dark:text-neutral-300 hover:border-neutral-300 dark:hover:border-neutral-700 hover:bg-neutral-50 dark:hover:bg-neutral-800/50 transition-all duration-200">
                {link.label}
              </a>
            ))}
          </div>
        </section>

        {/* Disclaimer */}
        <ContentSection className="max-w-4xl mx-auto px-4 sm:px-6 pb-8">
          <p className="text-xs text-neutral-400 dark:text-neutral-500 text-center">
            Zorcha is a trademark of its respective owner. Zapify is not affiliated with, endorsed by, or sponsored by Zorcha. Feature and pricing information is based on publicly available information and Zapify research as of June 6, 2026. Always check each provider's website for current pricing and plan details.
          </p>
        </ContentSection>
      </div>
    </div>
  )
}
